import { useEffect, useRef, useState } from "react";

import BaseButton from "../../../components/ui/BaseButton";
import BaseWrapper from "../../../components/ui/BaseWrapper";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createWorkOrder } from "../../../services/api";
import { showToast } from "../../../store/toast/toastSlice";
import { clearAuthStorage, navigateWithReload } from "../../../utils/helpers";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  approveAndPublishWorkOrder,
  publishDraftedWorkOrder,
  updateWorkOrderApi,
  uploadWorkOrderPurchaseOrder,
  uploadWorkOrderExpenseAttachments,
} from "../../../services/api/workOrder";
import {
  resetWorkOrderForm,
  updateWorkOrderFormValidation,
} from "../../../store/workOrder/workOrderSlice";
import BaseLoader from "../../../components/ui/BaseLoader";
import BaseConfirmModal from "../../../components/ui/BaseConfirmModal";
import { fetchCustomerCounterOffer } from "../../../services/api/offers";
import {
  deleteDigitalAsset,
  uploadWorkOrderCustomerFiles,
} from "../../../services/api/digitalAsset";
import useAuth from "../../../hooks/useAuth";
import { UserType } from "../../../utils/enum";
import { setClientFilters } from "../../../store/filters/clientFiltersSlice";
import { focusFirstInvalidField } from "../../../utils/formErrors";
const WorkOrderHeader = ({ isBannerVisible }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [isDataValidated, setIsDataValidated] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const params = useParams();
  const { authRole, auth } = useAuth();
  // Customers fill a reduced set of fields (client/project/engineer pay are hidden),
  // so bypass the payment/engineer-centric final gate — per-step validation still runs.
  const isCustomer = authRole === UserType.CUSTOMER;
  // Customers browse their work orders at /workOrders (CLIENT_WORK_ORDERS); the
  // client work board lives under /workBoard/workOrders, which customers have no
  // permission for — sending them there lands on Unauthorized.
  const workOrderListPath = isCustomer ? "/workOrders" : "/workBoard/workOrders";
  const isEdit = location.pathname.split("/")[2]?.toLowerCase() === "edit";
  const workOrder = useSelector((state) => state.workOrder);
  // A client editing a customer-raised ticket completes the marketplace details
  // and publishes it — with the FULL validation the customer's create skipped.
  // Never the customer themselves: they edit their own ticket and press Update,
  // and publishing is a client-only action they have no permission for.
  const isPendingApprovalEdit = isEdit && workOrder?.status === "PENDING_APPROVAL" && !isCustomer;

  // A counter offer the customer hasn't answered yet doesn't stop the client from
  // publishing, but they are warned first: it publishes on the current rates, and
  // the customer can no longer answer once the ticket leaves review.
  const [isPendingProposalWarningOpen, setIsPendingProposalWarningOpen] = useState(false);
  // Publishing saves this form first, so a proposal the customer accepted after the
  // form loaded would be overwritten by the rates still in it. Publish re-checks.
  const [isStaleRatesWarningOpen, setIsStaleRatesWarningOpen] = useState(false);
  const queryClient = useQueryClient();
  const proposalQueryKey = ["customerCounterOffer", Number(params.id)];
  const { data: customerCounterOfferData } = useQuery({
    queryKey: proposalQueryKey,
    queryFn: () => fetchCustomerCounterOffer(params.id),
    enabled: !!params.id && isPendingApprovalEdit,
  });

  // The proposal as it was when this page loaded the form: null when there was
  // none. Compared against the server's own timestamps, never the browser clock.
  const loadedProposalRef = useRef(undefined);
  useEffect(() => {
    if (loadedProposalRef.current === undefined && customerCounterOfferData) {
      const offer = customerCounterOfferData.data;
      loadedProposalRef.current = offer
        ? { status: offer.status, updatedAt: offer.updatedAt }
        : null;
    }
  }, [customerCounterOfferData]);
  // Validation failures jump the user to the step holding the offending field.
  // In edit mode that must stay on the edit route (and keep the :id) — otherwise
  // reviewing a customer ticket kicks the client into the create wizard.
  const stepPath = (step) =>
    isEdit ? `/workOrder/edit/${step}/${params.id}` : `/workOrder/create/${step}`;

  const {
    isPending: isPublishLoading,
    status,
    error,
    mutateAsync: workOrderMutation,
  } = useMutation({
    mutationFn: createWorkOrder,
    onSuccess: (data) => {},
  });

  const {
    isPending: isUploadWorkOrderCustomerFilesApiLoading,
    error: isUploadWorkOrderCustomerFilesApiError,
    mutateAsync: uploadWorkOrderCustomerFilesMutation,
  } = useMutation({
    mutationFn: uploadWorkOrderCustomerFiles,
  });

  const { mutateAsync: deleteCustomerFileMutation } = useMutation({
    mutationFn: deleteDigitalAsset,
  });

  const {
    isPending: isUploadWorkOrderPurchaseOrderFilesApiLoading,
    error: isUploadWorkOrderPurchaseOrderFilesApiError,
    mutateAsync: uploadWorkOrderPurchaseOrderFileMutation,
  } = useMutation({
    mutationFn: uploadWorkOrderPurchaseOrder,
  });

  const {
    isPending: isUpdatingLoading,
    error: isUpdatingError,
    mutateAsync: updateWorkOrderApiMutation,
  } = useMutation({
    mutationFn: updateWorkOrderApi,
    onSuccess: (data) => {},
  });

  const { isPending: isApproveAndPublishLoading, mutateAsync: approveAndPublishMutation } =
    useMutation({
      mutationFn: approveAndPublishWorkOrder,
    });

  const { isPending: isPublishDraftLoading, mutateAsync: publishDraftMutation } = useMutation({
    mutationFn: publishDraftedWorkOrder,
  });

  // Editing a DRAFT is still composition, not maintenance — so it keeps the
  // create flow's pair of actions (save it as a draft, or publish it) rather than
  // the single "Update" a live work order gets.
  const isDraftEdit = isEdit && workOrder?.status === "DRAFT";

  /**
   * Save the edits and put the work order on the marketplace. Mirrors
   * handleReviewAndPublish, but hits the draft endpoint: full validation first,
   * persist, then flip the status.
   */
  const handleSaveAndPublishDraft = async () => {
    try {
      if (!isWorkOrderFormValidated()) {
        return;
      }
      await updateWorkOrderApiMutation(buildEditPayload());
      await syncCustomerFilesFor(params.id);
      await uploadPurchaseOrdersFor(params.id, workOrder.payment?.pay?.purchaseOrders);
      await publishDraftMutation(params.id);
      dispatch(
        showToast({
          message: isCustomer
            ? "Work order has been submitted for acceptance."
            : "Work Order Published",
          type: "success",
        }),
      );
      dispatch(resetWorkOrderForm());
      // A customer publishing their own draft routes it to PENDING_APPROVAL for
      // client review, so land them on Submitted rather than a Published tab it
      // will never appear in.
      dispatch(
        setClientFilters({
          module: "workOrders",
          filterName: "status",
          data: [isCustomer ? "PENDING_APPROVAL" : "PUBLISHED"],
        }),
      );
      navigate(`${workOrderListPath}?filter=${isCustomer ? "PENDING_APPROVAL" : "PUBLISHED"}`);
    } catch (error) {
      console.error("Publish draft failed", error);
      const missingFields = error.response?.data?.missingFields;
      dispatch(
        showToast({
          message: missingFields?.length
            ? `Cannot publish. Please complete:\n${missingFields
                .map((field) => `• ${field}`)
                .join("\n")}`
            : error.response?.data?.message || error.message || "Failed to publish work order",
          type: "error",
          ...(missingFields?.length ? { autoClose: 8000, style: { whiteSpace: "pre-line" } } : {}),
        }),
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleWorkOrderUpdateOrPublish = async (isDraft) => {
    if (isEdit) {
      editWorkWork();
    } else {
      handleWorkOrderPublish(isDraft);
    }
  };

  const buildEditPayload = () => {
    setIsSaving(true);
    const formatedWorkOrderServices = workOrder?.summary?.workExperience?.workOrderServices?.map(
      (service) => {
        return { serviceId: service };
      },
    );
    let location =
      workOrder.scheduling.location.type === "SAVED_LOCATION"
        ? { addressId: workOrder.scheduling.location.addressId }
        : workOrder.scheduling.location.type === "REMOTE"
          ? { isRemote: true }
          : {
              ...workOrder.scheduling.location,
              isRemote: false,
              addressId: null,
            };
    return {
      id: params.id,
      ...workOrder.summary.overview,
      ...workOrder.summary.serviceDescription,
      paymentType: workOrder.payment.pay.paymentType,
      paymentTerms: workOrder.payment.pay.paymentTerms,
      currency: workOrder.payment.pay.currency,
      nonBusinessHourRate: parseFloat(workOrder.payment.pay.nonBusinessHourRate) || null,
      purchaseOrderNumber: workOrder.payment.pay.purchaseOrderNumber,
      hourlyRate: parseFloat(workOrder.payment.pay?.hourlyRate),
      halfDayRate: parseFloat(workOrder.payment.pay?.halfDayRate) || null,
      fullDayRate: parseFloat(workOrder.payment.pay?.fullDayRate) || null,
      fixedAmount: parseFloat(workOrder.payment.pay?.fixedAmount) || null,
      minimumHours: parseInt(workOrder.payment.pay?.minimumHours) || null,
      fthRate: parseFloat(workOrder.payment.pay?.fthRate) || null,
      additionalHourlyRate: parseFloat(workOrder.payment.pay?.additionalHourlyRate) || null,
      customHours: parseFloat(workOrder.payment.pay?.customHours) || null,
      customHoursRate: parseFloat(workOrder.payment.pay?.customHoursRate) || null,

      // Engineer pay was absent from this payload entirely, so nothing entered
      // in the edit wizard — including a draft being re-saved — ever reached the
      // API. Mirrors the engineer block in formatWorkOrderPayload.
      engineerPaymentType: workOrder.payment.pay?.engineerPaymentType,
      engineerCurrency: workOrder.payment.pay?.engineerCurrency || null,
      engineerHourlyRate: parseFloat(workOrder.payment.pay?.engineerHourlyRate) || null,
      engineerHalfDayRate: parseFloat(workOrder.payment.pay?.engineerHalfDayRate) || null,
      engineerFullDayRate: parseFloat(workOrder.payment.pay?.engineerFullDayRate) || null,
      engineerFixedAmount: parseFloat(workOrder.payment.pay?.engineerFixedAmount) || null,
      engineerFthRate: parseFloat(workOrder.payment.pay?.engineerFthRate) || null,
      engineerMinimumHours: parseInt(workOrder.payment.pay?.engineerMinimumHours) || null,
      engineerAdditionalHourlyRate:
        parseFloat(workOrder.payment.pay?.engineerAdditionalHourlyRate) || null,

      expenses: formatWorkOrderExpenses(workOrder.payment.expenses),
      ...location,
      ...workOrder.scheduling.schedule,
      orderEndDate: workOrder.scheduling.schedule.orderEndDate,
      timeZone: workOrder?.scheduling?.schedule?.timeZone,
      categoryId: workOrder?.summary?.workExperience?.categoryId,
      vendorId: workOrder?.summary?.workExperience?.vendorId,
      workOrderServices: formatedWorkOrderServices,
      qualificationSubTypes: formatWorkOrderQualifications(workOrder.scheduling.qualifications),
      // On Review & Publish the client is the default Work Order Manager, but may
      // pick another company user; fall back to the publishing client when unset.
      workOrderManagerId:
        parseInt(workOrder.scheduling.contacts.workOrderManagerId) || auth?.user?.id || null,
      customerFiles: (workOrder.summary?.customerFiles?.files || [])
        .filter((file) => file.id)
        .map((file) => ({ id: parseInt(file.id) })),
    };
  };

  const editWorkWork = async () => {
    try {
      // Saving an existing draft goes through here, so it needs the same guard.
      if (isDraftEdit && !assertQualificationsStorable()) {
        return;
      }
      await updateWorkOrderApiMutation(buildEditPayload());

      await syncCustomerFilesFor(params.id);
      await uploadPurchaseOrdersFor(params.id, workOrder.payment?.pay?.purchaseOrders);
      await uploadExpenseFilesFor(params.id, workOrder.payment.expenses);
      await uploadExpenseFilesFor(params.id, workOrder.payment.engineerExpenses);

      dispatch(
        showToast({
          message: isDraftEdit ? "Draft Saved" : "Work Order Updated",
          type: "success",
        }),
      );

      dispatch(resetWorkOrderForm());

      // Saving a draft returns to the Draft tab; updating a live work order keeps
      // the listing's default. Hardcoding /workBoard/workOrders here used to send
      // customers to a board they have no permission for.
      if (isDraftEdit) {
        dispatch(
          setClientFilters({
            module: "workOrders",
            filterName: "status",
            data: ["DRAFT"],
          }),
        );
        navigate(`${workOrderListPath}?filter=DRAFT`);
      } else {
        navigate(workOrderListPath);
      }
    } catch (error) {
      dispatch(
        showToast({
          message: error.response?.data?.message,
          type: "error",
        }),
      );
    } finally {
      setIsSaving(false);
    }
  };

  // Before the client publishes a customer's ticket, look at the proposal as it is
  // on the server now: if the customer accepted it since this form loaded, the
  // form's rates are stale, so stop and offer a reload instead of overwriting the
  // accepted ones; if it is still pending, warn first (unless already confirmed).
  const handlePendingApprovalPublish = async ({ isPendingProposalConfirmed = false } = {}) => {
    let latestProposal;
    try {
      latestProposal = (
        await queryClient.fetchQuery({
          queryKey: proposalQueryKey,
          queryFn: () => fetchCustomerCounterOffer(params.id),
          staleTime: 0,
        })
      )?.data;
    } catch (error) {
      dispatch(
        showToast({
          message: "Couldn't check the proposal's status. Please try again.",
          type: "error",
        }),
      );
      return;
    }

    const loaded = loadedProposalRef.current;
    const wasAcceptedSinceLoad =
      // An accepted proposal is stored as ASSIGNED.
      latestProposal?.status === "ASSIGNED" &&
      loaded !== undefined &&
      !(loaded?.status === "ASSIGNED" && loaded?.updatedAt === latestProposal.updatedAt);

    if (wasAcceptedSinceLoad) {
      setIsStaleRatesWarningOpen(true);
      return;
    }
    if (latestProposal?.status === "PENDING" && !isPendingProposalConfirmed) {
      setIsPendingProposalWarningOpen(true);
      return;
    }
    await handleReviewAndPublish({ isPendingProposalConfirmed });
  };

  // Client review path: run the FULL create validations (client is never a
  // customer, so nothing is skipped), persist the completed details, then publish
  // the customer-raised ticket to the marketplace.
  const handleReviewAndPublish = async ({ isPendingProposalConfirmed = false } = {}) => {
    try {
      if (!isWorkOrderFormValidated()) {
        return;
      }
      await updateWorkOrderApiMutation(buildEditPayload());
      await syncCustomerFilesFor(params.id);
      await uploadPurchaseOrdersFor(params.id, workOrder.payment?.pay?.purchaseOrders);
      await approveAndPublishMutation({
        workOrderId: params.id,
        // The server refuses to publish over an unanswered proposal unless told the
        // client confirmed the warning above.
        publishWithCustomerRates: isPendingProposalConfirmed,
      });
      dispatch(
        showToast({
          message: "Work Order Published",
          type: "success",
        }),
      );
      dispatch(resetWorkOrderForm());
      navigate(`/workBoard/workOrders`);
    } catch (error) {
      // The server found a proposal still pending that the pre-check above missed
      // (sent between the check and the publish): show the same warning rather
      // than an error, and the confirm resends with publishWithCustomerRates.
      if (
        error.response?.status === 409 &&
        error.response?.data?.code === "COUNTER_OFFER_PENDING"
      ) {
        setIsPendingProposalWarningOpen(true);
        return;
      }
      // Fall back to error.message: a non-Axios failure (e.g. a TypeError in the
      // validator) has no `response`, which previously toasted an empty string and
      // made the button look completely inert.
      console.error("Review & Publish failed", error);
      dispatch(
        showToast({
          message: error.response?.data?.message || error.message || "Failed to publish work order",
          type: "error",
        }),
      );
    }
  };

  async function blobUrlToFile(blobUrl, fileName) {
    const response = await fetch(blobUrl); // fetch blob from URL
    const blob = await response.blob(); // convert to blob
    return new File([blob], fileName, { type: blob.type }); // make File object
  }

  const uploadExpenseFilesFor = async (workOrderId, expenseList) => {
    const withFiles = (expenseList || []).filter((e) => e.attachment?.url?.startsWith("blob:"));
    if (!withFiles.length || !workOrderId) return;

    const formData = new FormData();
    formData.append(
      "expenses",
      JSON.stringify(withFiles.map((e) => ({ expenseKey: e.expenseKey }))),
    );

    for (const expense of withFiles) {
      const fileObj = await blobUrlToFile(expense.attachment.url, expense.attachment.fileName);
      const fileWithKey = new File([fileObj], `${expense.expenseKey}__-${fileObj.name}`, {
        type: fileObj.type,
      });
      formData.append("files", fileWithKey);
    }

    await uploadWorkOrderExpenseAttachments({ workOrderId, formData });
  };

  /**
   * Persists what the Documents card changed. Mirrors EditCustomerFilesModal:
   * files the user dropped are uploaded (the endpoint attaches them itself) and
   * ones they removed are deleted, since an update only ever attaches the ids it
   * is given. Kept files ride along in buildEditPayload and are a no-op.
   */
  const syncCustomerFilesFor = async (workOrderId) => {
    if (!workOrderId) return;

    const currentFiles = workOrder.summary?.customerFiles?.files || [];
    const baselineIds = workOrder.summary?.customerFilesBaseline || [];

    const keptIds = new Set(currentFiles.filter((file) => file.id).map((file) => Number(file.id)));
    const removedIds = baselineIds.filter((id) => !keptIds.has(Number(id)));

    for (const id of removedIds) {
      await deleteCustomerFileMutation(id);
    }

    // Anything without an id is a File the user just dropped in.
    const newFiles = currentFiles.filter((file) => !file.id);
    if (newFiles.length > 0) {
      const formData = new FormData();
      newFiles.forEach((file) => formData.append("files", file));
      await uploadWorkOrderCustomerFilesMutation({ workOrderId, formData });
    }
  };

  // Purchase orders never travel in the work order payload — they have their own
  // upsert endpoint, keyed on each PO's uuid: one already carrying a string id is
  // updated in place, one still holding the client-side Date.now() number is
  // created. Deletions go through deleteWorkOrderPurchaseOrder from PayForm, so
  // sending the current list is safe to repeat on every save.
  const uploadPurchaseOrdersFor = async (workOrderId, purchaseOrders) => {
    const poList = purchaseOrders || [];
    if (!poList.length || !workOrderId) return;

    const formData = new FormData();

    const poData = poList.map((po) => ({
      ...(typeof po.id === "string" ? { id: po.id } : {}),
      number: po.number,
      uid: crypto.randomUUID(),
    }));

    formData.append("purchaseOrders", JSON.stringify(poData));

    // Only a blob: url is a file the user just picked; an https: one is the
    // already-stored attachment coming back from the API.
    for (let i = 0; i < poData.length; i++) {
      const attachment = poList[i].attachment;
      if (!attachment?.url?.startsWith("blob:")) continue;

      const fileObj = await blobUrlToFile(attachment.url, attachment.fileName);
      const fileWithUID = new File([fileObj], `${poData[i].uid}__-${fileObj.name}`, {
        type: fileObj.type,
      });

      formData.append("files", fileWithUID);
    }

    await uploadWorkOrderPurchaseOrderFileMutation({ workOrderId, formData });
  };

  const handleWorkOrderPublish = async (isDraft) => {
    try {
      if (isDraft && !assertQualificationsStorable()) {
        return;
      }
      // A draft can be saved at any point, however incomplete — only a title is
      // required, and the backend re-checks the rest when it is published.
      if (!isDraft && !isCustomer && !isWorkOrderFormValidated()) {
        return;
      }
      setIsSaving(true);
      const payload = formatWorkOrderPayload(isDraft);
      const workOrderResponse = await workOrderMutation(payload);
      if (workOrder.summary?.customerFiles?.files.length > 0) {
        const formData = new FormData();
        workOrder.summary?.customerFiles?.files.forEach((file) => formData.append("files", file));

        await uploadWorkOrderCustomerFilesMutation({
          workOrderId: workOrderResponse?.id,
          formData,
        });
      }

      await uploadPurchaseOrdersFor(workOrderResponse?.id, workOrder.payment?.pay?.purchaseOrders);

      await uploadExpenseFilesFor(workOrderResponse?.id, workOrder.payment.expenses);
      await uploadExpenseFilesFor(workOrderResponse?.id, workOrder.payment.engineerExpenses);

      dispatch(
        showToast({
          // A customer's work order is created as PENDING_APPROVAL (not published to
          // the marketplace), so it's submitted for a client to review, not published.
          message: isDraft
            ? "Work Order Drafted"
            : isCustomer
              ? "Work order has been submitted for acceptance."
              : "Work Order Published",
          type: isDraft ? "info" : "success",
        }),
      );

      dispatch(resetWorkOrderForm());

      // Customers have their own listing (CLIENT_WORK_ORDERS -> /workOrders); the
      // client work board lives under /workBoard/workOrders.
      if (isCustomer && !isDraft) {
        // Land on the Submitted tab so the customer sees the ticket they just
        // raised. The listing reads the active tab from the redux filter, falling
        // back to the `filter` query param, so both are set here.
        dispatch(
          setClientFilters({
            module: "workOrders",
            filterName: "status",
            data: ["PENDING_APPROVAL"],
          }),
        );
        navigate(`/workOrders?filter=PENDING_APPROVAL`);
      } else if (isDraft) {
        // Same idea for a draft: land on the Draft tab so the work order just
        // saved is actually on screen, rather than on a Published tab it isn't in.
        dispatch(
          setClientFilters({
            module: "workOrders",
            filterName: "status",
            data: ["DRAFT"],
          }),
        );
        navigate(`${workOrderListPath}?filter=DRAFT`);
      } else {
        navigate(workOrderListPath);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        clearAuthStorage();
        navigateWithReload("/getStarted");
      }
      if (typeof error.response?.data?.message === "string") {
        dispatch(
          showToast({
            message: error.response?.data?.message,
            type: "error",
          }),
        );

        return;
      }

      for (let i = 0; i < error?.response?.data?.message.length; i++) {
        const errorMessage = error?.response?.data.message[i];
        dispatch(
          showToast({
            message: errorMessage,
            type: "error",
          }),
        );
      }
    } finally {
      setIsSaving(false);
    }
  };

  const isWorkOrderFormValidated = () => {
    if (workOrder.payment?.tasks?.length > 0) {
      workOrder.payment.tasks.forEach((task) => {
        if (task.taskType === "CUSTOM") {
          return false;
        }
      });
    }

    if (workOrder.summary.overview.title === "") {
      dispatch(
        updateWorkOrderFormValidation({
          fieldName: "title",
          data: true,
        }),
      );

      dispatch(
        showToast({
          message: "Title is required",
          type: "error",
        }),
      );

      navigate(stepPath("summary"));
      return false;
    }
    if (!workOrder.summary.overview.clientId) {
      dispatch(
        updateWorkOrderFormValidation({
          fieldName: "clientId",
          data: true,
        }),
      );

      dispatch(
        showToast({
          message: "Client is required",
          type: "error",
        }),
      );

      navigate(stepPath("summary"));
      return false;
    }
    // Project and both teams are hidden from customers and derived from the
    // entity they pick, so gating on them blocked a customer on fields they
    // cannot see. Mirrors OverviewForm.isValid(), which already skips these.
    if (!isCustomer && !workOrder.summary.overview.projectId) {
      dispatch(
        updateWorkOrderFormValidation({
          fieldName: "projectId",
          data: true,
        }),
      );

      dispatch(
        showToast({
          message: "Project is required",
          type: "error",
        }),
      );

      navigate(stepPath("summary"));
      return false;
    }
    if (!isCustomer && !workOrder.summary.overview.teamId) {
      dispatch(
        updateWorkOrderFormValidation({
          fieldName: "teamId",
          data: true,
        }),
      );

      dispatch(
        showToast({
          message: "Service Delivery Team is required. Link one to the selected client.",
          type: "error",
        }),
      );

      navigate(stepPath("summary"));
      return false;
    }
    if (!isCustomer && !workOrder.summary.overview.secondaryTeamId) {
      dispatch(
        updateWorkOrderFormValidation({
          fieldName: "secondaryTeamId",
          data: true,
        }),
      );

      dispatch(
        showToast({
          message: "Operations Team is required. Link one to the selected client.",
          type: "error",
        }),
      );

      navigate(stepPath("summary"));
      return false;
    }
    if (workOrder.scheduling.qualifications?.length > 0) {
      for (let i = 0; i < (workOrder.scheduling?.qualifications?.length ?? 0); i++) {
        const qualification = workOrder.scheduling.qualifications[i];

        if (!qualification.qualificationTypeId) {
          dispatch(
            updateWorkOrderFormValidation({
              fieldName: `qualificationTypeId`,
              data: true,
            }),
          );
          dispatch(
            showToast({
              message: `Please select a qualification type`,
              type: "error",
            }),
          );
          navigate(stepPath("scheduling"));
          return false;
        }

        if (
          !qualification.qualificationSubTypes ||
          qualification.qualificationSubTypes.length === 0
        ) {
          dispatch(
            updateWorkOrderFormValidation({
              fieldName: `qualificationSubTypes`,
              data: true,
            }),
          );
          dispatch(
            showToast({
              message: `Please select at least one sub-type for qualification`,
              type: "error",
            }),
          );
          navigate(stepPath("scheduling"));
          return false;
        }
      }
    } else {
      dispatch(
        updateWorkOrderFormValidation({
          fieldName: "qualificationTypeId",
          data: true,
        }),
      );

      dispatch(
        showToast({
          message: "Qualification Type is required",
          type: "error",
        }),
      );

      navigate(stepPath("summary"));
      return false;
    }
    // Work Experience (technology stack, vendor, products) is not rendered for
    // customers — Summary omits the whole form — and the server's customer
    // submit check deliberately does not require it either.
    if (!isCustomer && workOrder.summary.workExperience.categoryId === "") {
      dispatch(
        updateWorkOrderFormValidation({
          fieldName: "categoryId",
          data: true,
        }),
      );

      dispatch(
        showToast({
          message: "Technology Stacks are required",
          type: "error",
        }),
      );

      navigate(stepPath("summary"));
      return false;
    }
    if (!isCustomer && workOrder.summary.workExperience.vendorId === "") {
      dispatch(
        updateWorkOrderFormValidation({
          fieldName: "vendorId",
          data: true,
        }),
      );

      dispatch(
        showToast({
          message: "Category is required",
          type: "error",
        }),
      );

      navigate(stepPath("summary"));
      return false;
    }
    if (!isCustomer && (workOrder.summary?.workExperience?.workOrderServices?.length ?? 0) < 1) {
      dispatch(
        updateWorkOrderFormValidation({
          fieldName: "workOrderServices",
          data: true,
        }),
      );

      dispatch(
        showToast({
          message: "Skills are required",
          type: "error",
        }),
      );

      navigate(stepPath("summary"));
      return false;
    }
    if (workOrder.scheduling.location.type === "EXACT_LOCATION") {
      if (
        workOrder.scheduling.location.latitude === "" ||
        workOrder.scheduling.location.longitude === ""
      ) {
        dispatch(
          updateWorkOrderFormValidation({
            fieldName: "addressLine1",
            data: true,
          }),
        );
        dispatch(
          showToast({
            message: "Address is required",
            type: "error",
          }),
        );

        navigate(stepPath("scheduling"));
        return false;
      }
    } else if (workOrder.scheduling.location.type === "SAVED_LOCATION") {
      if (workOrder.scheduling.location.addressId === "") {
        dispatch(
          updateWorkOrderFormValidation({
            fieldName: "addressId",
            data: true,
          }),
        );

        dispatch(
          showToast({
            message: "Address is required",
            type: "error",
          }),
        );

        navigate(stepPath("scheduling"));
        return false;
      }
    }
    const invalidPO = (workOrder.payment?.pay?.purchaseOrders ?? []).some(
      (po) => po.attachment?.url && (!po.number || po.number.trim() === ""),
    );

    if (invalidPO) {
      dispatch(
        updateWorkOrderFormValidation({
          fieldName: "purchaseOrdersErrorMsg",
          data: "Purchase Order number is required if a file is attached",
        }),
      );
      dispatch(
        updateWorkOrderFormValidation({
          fieldName: "purchaseOrders",
          data: true,
        }),
      );
      dispatch(
        showToast({
          message: "Each attached file must have a Purchase Order number",
          type: "error",
        }),
      );
      navigate(stepPath("payment"));
      return false;
    }
    if (
      (workOrder.payment.pay.paymentType === "FULL_DAY" ||
        workOrder.payment.pay.paymentType === "HALF_DAY" ||
        workOrder.payment.pay.paymentType === "HOURLY" ||
        workOrder.payment.pay.paymentType === "FTH") &&
      (workOrder.payment.pay.hourlyRate === null || workOrder.payment.pay.hourlyRate === "")
    ) {
      dispatch(
        updateWorkOrderFormValidation({
          fieldName: "hourlyRateErrorMsg",
          data: "Hourly rate is required",
        }),
      );
      dispatch(
        updateWorkOrderFormValidation({
          fieldName: "hourlyRate",
          data: true,
        }),
      );
      dispatch(
        showToast({
          message: "Hourly rate is required",
          type: "error",
        }),
      );

      navigate(stepPath("payment"));
      return false;
    }
    if (workOrder?.payment.pay.enableCustomRates === true) {
      const {
        engineerCurrency,
        engineerPaymentType,
        engineerHourlyRate,
        engineerFullDayRate,
        engineerHalfDayRate,
        engineerFixedAmount,
        engineerMinimumHours,
        engineerAdditionalHourlyRate,
      } = workOrder.payment.pay;

      if (engineerMinimumHours > 0 && engineerAdditionalHourlyRate <= 0) {
        dispatch(
          showToast({
            message: "Additional Hourly rate is required",
            type: "error",
          }),
        );
        return false;
      }

      if (engineerCurrency) {
        if (
          (engineerPaymentType === "HOURLY" && !engineerHourlyRate) ||
          (engineerPaymentType === "FULL_DAY" && !engineerFullDayRate) ||
          (engineerPaymentType === "HALF_DAY" && !engineerHalfDayRate)
        ) {
          dispatch(
            showToast({
              message: "Engineer rates are required",
              type: "error",
            }),
          );
          return false;
        }
      }

      if (
        (engineerCurrency === null || engineerCurrency === "") &&
        ((engineerPaymentType === "HOURLY" && engineerHourlyRate) ||
          (engineerPaymentType === "FULL_DAY" && engineerFullDayRate) ||
          (engineerPaymentType === "HALF_DAY" && engineerHalfDayRate))
      ) {
        dispatch(
          showToast({
            message: "Engineer Currency is required",
            type: "error",
          }),
        );
        return false;
      }
      if (
        ((workOrder.payment.pay.engineerPaymentType === "FULL_DAY" &&
          workOrder.payment.pay.engineerFullDayRate) ||
          (workOrder.payment.pay.engineerPaymentType === "HALF_DAY" &&
            workOrder.payment.pay.engineerHalfDayRate) ||
          (workOrder.payment.pay.engineerPaymentType === "HOURLY" &&
            workOrder.payment.pay.engineerHourlyRate)) &&
        (workOrder.payment.pay.engineerHourlyRate === null ||
          workOrder.payment.pay.engineerHourlyRate === "")
      ) {
        dispatch(
          updateWorkOrderFormValidation({
            fieldName: "engineerHourlyRateErrorMsg",
            data: "Hourly rate is required",
          }),
        );
        dispatch(
          updateWorkOrderFormValidation({
            fieldName: "engineerHourlyRate",
            data: true,
          }),
        );
        dispatch(
          showToast({
            message: "Engineer Hourly rate is required",
            type: "error",
          }),
        );
        navigate(stepPath("payment"));
        return false;
      }

      /* if (
        engineerPaymentType === "HOURLY" &&
        engineerHourlyRate 
        && (engineerMinimumHours === null || engineerMinimumHours === "" || engineerMinimumHours < 1)
      ) {
        dispatch(
          updateWorkOrderFormValidation({
            fieldName: "engineerMinimumHours",
            data: true,
          }),
        );
        dispatch(
          showToast({
            message: "Engineer minimum 1 hour is required",
            type: "error",
          }),
        );
        navigate(stepPath("payment"));
        return false;
      } */

      /* if (
        engineerPaymentType === "HOURLY" &&
        engineerMinimumHours !== null &&
        engineerMinimumHours !== "" &&
        engineerMinimumHours < 1
      ) {
        dispatch(
          updateWorkOrderFormValidation({
            fieldName: "engineerMinimumHours",
            data: true,
          }),
        );
        dispatch(
          showToast({
            message: "Engineer minimum hours cannot be less than 1",
            type: "error",
          }),
        );
        navigate(stepPath("payment"));
        return false;
      } */
      if (
        engineerPaymentType === "FIXED" &&
        (engineerFixedAmount === null || engineerFixedAmount === "" || engineerFixedAmount <= 0)
      ) {
        dispatch(
          updateWorkOrderFormValidation({
            fieldName: "engineerFixedAmountErrorMsg",
            data: "Fixed Amount rate cannot be less than $1",
          }),
        );
        dispatch(
          updateWorkOrderFormValidation({
            fieldName: "engineerFixedAmount",
            data: true,
          }),
        );
        dispatch(
          showToast({
            message: "Fixed Amount rate cannot be less than $1",
            type: "error",
          }),
        );
        navigate(stepPath("payment"));
        return false;
      }
    }
    if (workOrder.scheduling.schedule.orderStartDate === "") {
      dispatch(
        updateWorkOrderFormValidation({
          fieldName: "orderStartDate",
          data: true,
        }),
      );

      dispatch(
        showToast({
          message: "Schedule is required.",
          type: "error",
        }),
      );

      navigate(stepPath("scheduling"));
      return false;
    }

    return true;
  };

  // Gate the customer's submit on the fields THEY fill (title, location, schedule,
  // and their own rate) — client/engineer fields are out of scope for them.
  const isCustomerPublishReady = () => {
    const { overview } = workOrder.summary;
    const { location, schedule } = workOrder.scheduling;
    const { pay } = workOrder.payment;

    if (!overview.title?.trim()) return false;
    if (!overview.clientId) return false;

    if (location.type === "EXACT_LOCATION") {
      if (!location.latitude || !location.longitude) return false;
    } else if (location.type === "SAVED_LOCATION") {
      if (!location.addressId) return false;
    }

    if (!schedule.orderStartDate || !schedule.orderStartTime) return false;

    if (!pay.currency) return false;

    const rateByType = {
      HOURLY: pay.hourlyRate,
      HALF_DAY: pay.halfDayRate,
      FULL_DAY: pay.fullDayRate,
      FIXED: pay.fixedAmount,
      FTH: pay.fthRate,
      CUSTOM_HOURS: pay.customHoursRate,
    };
    const rate = rateByType[pay.paymentType];
    if (rate === null || rate === undefined || rate === "" || Number(rate) <= 0) return false;

    // The base rate alone isn't enough: each payment type carries its own extra
    // required fields, and customers fill in the same Payment form as clients —
    // so mirror what isPublishedButtonDisabled() demands below.
    if (pay.paymentType === "HOURLY") {
      if (!pay.minimumHours || Number(pay.minimumHours) < 1) return false;
      if (pay.enableClientAdditionalHourRate && !(Number(pay.additionalHourlyRate) > 0)) {
        return false;
      }
      if (pay.enableNonBusinessHourRate && !(Number(pay.nonBusinessHourRate) > 0)) return false;
    }

    // Half day, full day and fixed-two-hours all bill their extra hours at the
    // hourly rate, which the form labels "Additional Hour Rate".
    if (["HALF_DAY", "FULL_DAY", "FTH"].includes(pay.paymentType)) {
      if (!(Number(pay.hourlyRate) > 0)) return false;
    }

    if (pay.paymentType === "CUSTOM_HOURS") {
      if (pay.customHours === null || pay.customHours === undefined || pay.customHours === "") {
        return false;
      }
    }

    return true;
  };

  const isPublishedButtonDisabled = () => {
    if (workOrder.summary.overview.title === "") {
      return false;
    }
    if (workOrder.scheduling.location.type === "EXACT_LOCATION") {
      if (
        workOrder.scheduling.location.latitude === "" ||
        workOrder.scheduling.location.longitude === ""
      ) {
        return false;
      }
    } else if (workOrder.scheduling.location.type === "SAVED_LOCATION") {
      if (workOrder.scheduling.location.addressId === "") {
        return false;
      }
    }
    if (
      workOrder.payment.pay.paymentTerms === "" ||
      workOrder.payment.pay.paymentTerms === null ||
      workOrder.payment.pay.paymentTerms === undefined
    ) {
      return false;
    }
    if (workOrder.payment.pay.currency === "" || workOrder.payment.pay.currency === null) {
      return false;
    }
    if (workOrder.payment.pay.paymentType === "HOURLY") {
      if (workOrder.payment.pay.hourlyRate === null || workOrder.payment.pay.hourlyRate <= 0) {
        return false;
      }
      /* if (workOrder.payment.pay.hourlyRate < 20) {
        return false;
      } */
      if (
        workOrder.payment.pay.minimumHours === null ||
        workOrder.payment.pay.minimumHours === "" ||
        workOrder.payment.pay.minimumHours < 1
      ) {
        return false;
      }

      if (
        workOrder.payment.pay.enableClientAdditionalHourRate &&
        workOrder.payment.pay.additionalHourlyRate <= 0
      ) {
        return false;
      }
      if (
        workOrder.payment.pay.enableNonBusinessHourRate &&
        workOrder.payment.pay.nonBusinessHourRate <= 0
      ) {
        return false;
      }
    }
    if (workOrder.payment.pay.paymentType === "HALF_DAY") {
      if (
        workOrder.payment.pay.halfDayRate === null ||
        workOrder.payment.pay.halfDayRate === ""
        // || workOrder.payment.pay.halfDayRate < 50
      ) {
        return false;
      }
      if (workOrder.payment.pay.hourlyRate === null || workOrder.payment.pay.hourlyRate <= 0) {
        return false;
      }
    }
    if (workOrder.payment.pay.paymentType === "FULL_DAY") {
      if (workOrder.payment.pay.fullDayRate === null || workOrder.payment.pay.fullDayRate === "") {
        return false;
      }
      if (workOrder.payment.pay.hourlyRate === null || workOrder.payment.pay.hourlyRate <= 0) {
        return false;
      }
    }
    if (workOrder.payment.pay.paymentType === "FIXED") {
      if (
        workOrder.payment.pay.fixedAmount === null ||
        workOrder.payment.pay.fixedAmount === "" ||
        workOrder.payment.pay.fixedAmount <= 0
      ) {
        return false;
      }
    }
    if (workOrder.payment.pay.paymentType === "FTH") {
      if (workOrder.payment.pay?.fthRate === null || workOrder.payment.pay?.fthRate === "") {
        return false;
      }
      if (workOrder.payment.pay.hourlyRate === null || workOrder.payment.pay.hourlyRate <= 0) {
        return false;
      }
    }
    if (workOrder.payment.pay.paymentType === "CUSTOM_HOURS") {
      if (
        workOrder.payment.pay?.customHours === null ||
        workOrder.payment.pay?.customHours === ""
      ) {
        return false;
      }
      if (
        workOrder.payment.pay.customHoursRate === null ||
        workOrder.payment.pay.customHoursRate <= 0
      ) {
        return false;
      }
    }
    if (workOrder.payment.pay.engineerPaymentType === "") {
      return false;
    }
    const { enableCustomRates } = workOrder.payment.pay;
    if (enableCustomRates === true) {
      if (
        workOrder.payment.pay.engineerPaymentType === null ||
        workOrder.payment.pay.engineerPaymentType === ""
      ) {
        return false;
      }

      if (workOrder.payment.pay.engineerPaymentType === "HOURLY") {
        if (
          workOrder.payment.pay.engineerHourlyRate === null ||
          workOrder.payment.pay.engineerHourlyRate <= 0
        ) {
          return false;
        }

        /*  if (
          workOrder.payment.pay.engineerMinimumHours === null ||
          workOrder.payment.pay.engineerMinimumHours === "" ||
          workOrder.payment.pay.engineerMinimumHours < 1
        ) {
          return false;
        } */

        if (
          workOrder.payment.pay.enableEngineerAdditionalHourRate &&
          workOrder.payment.pay.engineerAdditionalHourlyRate <= 0
        ) {
          return false;
        }

        if (
          workOrder.payment.pay.engineerMinimumHours > 0 &&
          workOrder.payment.pay.engineerAdditionalHourlyRate <= 0
        ) {
          return false;
        }
      }

      if (workOrder.payment.pay.engineerPaymentType === "HALF_DAY") {
        if (
          workOrder.payment.pay.engineerHalfDayRate === null ||
          workOrder.payment.pay.engineerHalfDayRate === ""
          // || workOrder.payment.pay.halfDayRate < 50
        ) {
          return false;
        }
        if (
          workOrder.payment.pay.engineerHourlyRate === null ||
          workOrder.payment.pay.engineerHourlyRate <= 0
        ) {
          return false;
        }
      }
      if (workOrder.payment.pay.engineerPaymentType === "FULL_DAY") {
        if (
          workOrder.payment.pay.engineerFullDayRate === null ||
          workOrder.payment.pay.engineerFullDayRate === ""
        ) {
          return false;
        }
        if (
          workOrder.payment.pay.engineerHourlyRate === null ||
          workOrder.payment.pay.engineerHourlyRate <= 0
        ) {
          return false;
        }
      }

      if (workOrder.payment.pay.engineerPaymentType === "FIXED") {
        if (
          workOrder.payment.pay.engineerFixedAmount === null ||
          workOrder.payment.pay.engineerFixedAmount === "" ||
          workOrder.payment.pay.engineerFixedAmount <= 0
        ) {
          return false;
        }
      }
    }
    if (
      workOrder.scheduling.schedule.orderStartDate === "" ||
      workOrder.scheduling.schedule.orderStartTime === ""
    ) {
      return false;
    }

    if (
      workOrder.summary.workExperience.categoryId === "" ||
      workOrder.summary.workExperience.vendorId === "" ||
      workOrder.summary.workExperience.workOrderServices.length === 0
    ) {
      return false;
    }

    if (workOrder.payment.tasks.length > 0) {
      let error = true;
      workOrder.payment.tasks.forEach((task) => {
        if (task.taskType === "CUSTOM") {
          console.log("here error");
          error = false;
        }
      });
      return error;
    }

    return true;
  };

  // A draft can be saved at any stage — a title is the only thing it needs, so
  // it stays identifiable in the work order list. Everything else (schedule,
  // location, teams, pay) is filled in later and enforced on publish.
  const isDraftReady = () => (workOrder.summary?.overview?.title || "").trim() !== "";

  const formatWorkOrderPayload = (isDraft) => {
    const formatedWorkOrderServices = workOrder.summary.workExperience?.workOrderServices?.map(
      (service) => {
        return { serviceId: service };
      },
    );

    let location =
      workOrder.scheduling.location.type === "SAVED_LOCATION"
        ? { addressId: workOrder.scheduling.location.addressId }
        : workOrder.scheduling.location.type === "REMOTE"
          ? { isRemote: true }
          : { ...workOrder.scheduling.location, addressId: null };
    const customerFiles = workOrder.summary?.customerFiles?.files || [];
    const isCustomRatesEnabled = workOrder.payment.pay?.enableCustomRates;
    const payload = {
      ...workOrder.summary.overview,

      incidentId: workOrder.summary.overview.incidentId,
      ...workOrder.summary.serviceDescription,
      categoryId: workOrder.summary.workExperience.categoryId,
      vendorId: workOrder.summary.workExperience.vendorId,
      workOrderServices: formatedWorkOrderServices,
      ...workOrder.scheduling.schedule,
      timeZone: workOrder.scheduling.schedule.timeZone,
      ...location,
      qualificationSubTypes: formatWorkOrderQualifications(workOrder.scheduling.qualifications),
      expenses: formatWorkOrderExpenses(workOrder.payment.expenses),
      paymentType: workOrder.payment.pay.paymentType,
      paymentTerms: workOrder.payment.pay.paymentTerms,
      currency: workOrder.payment.pay.currency,
      nonBusinessHourRate: workOrder.payment.pay.enableNonBusinessHourRate
        ? parseFloat(workOrder.payment.pay.nonBusinessHourRate)
        : null,
      purchaseOrderNumber: workOrder.payment.pay.purchaseOrderNumber,
      hourlyRate: parseFloat(workOrder.payment.pay?.hourlyRate) || null,
      additionalHourlyRate: workOrder.payment.pay.enableClientAdditionalHourRate
        ? parseFloat(workOrder.payment.pay?.additionalHourlyRate)
        : null,
      halfDayRate: parseFloat(workOrder.payment.pay?.halfDayRate) || null,
      fullDayRate: parseFloat(workOrder.payment.pay?.fullDayRate) || null,
      fthRate: parseFloat(workOrder.payment.pay?.fthRate) || null,
      customHours: parseFloat(workOrder.payment.pay?.customHours) || null,
      customHoursRate: parseFloat(workOrder.payment.pay?.customHoursRate) || null,
      fixedAmount: parseFloat(workOrder.payment.pay?.fixedAmount) || null,
      minimumHours: parseInt(workOrder.payment.pay?.minimumHours) || null,

      engineerExpenses: formatWorkOrderExpenses(workOrder.payment.engineerExpenses),

      engineerPaymentType: workOrder.payment.pay.engineerPaymentType,

      engineerCurrency: !isCustomRatesEnabled
        ? null
        : workOrder.payment.pay.engineerCurrency || null,

      engineerHourlyRate: !isCustomRatesEnabled
        ? null
        : parseFloat(workOrder.payment.pay?.engineerHourlyRate) || null,

      engineerAdditionalHourlyRate: workOrder.payment.pay.enableEngineerAdditionalHourRate
        ? parseFloat(workOrder.payment.pay?.engineerAdditionalHourlyRate)
        : null,

      engineerHalfDayRate: !isCustomRatesEnabled
        ? null
        : parseFloat(workOrder.payment.pay?.engineerHalfDayRate) || null,

      engineerFullDayRate: !isCustomRatesEnabled
        ? null
        : parseFloat(workOrder.payment.pay?.engineerFullDayRate) || null,

      engineerFthRate: !isCustomRatesEnabled
        ? null
        : parseFloat(workOrder.payment.pay?.engineerFthRate) || null,

      engineerFixedAmount: !isCustomRatesEnabled
        ? null
        : parseFloat(workOrder.payment.pay?.engineerFixedAmount) || null,

      engineerMinimumHours: !isCustomRatesEnabled
        ? null
        : parseInt(workOrder.payment.pay?.engineerMinimumHours) || null,

      isDraft,
      tasks: formatWorkOrderTasks(workOrder.payment.tasks),
      contacts: formatWorkOrderContacts(workOrder.scheduling.contacts.contacts),
      // Customers don't set a manager (field hidden); leave it null for a client to
      // assign at publish. Guard against parseInt("") -> NaN for everyone else.
      workOrderManagerId: isCustomer
        ? null
        : parseInt(workOrder.scheduling.contacts.workOrderManagerId) || null,
      customerFiles: customerFiles
        .filter((file) => file.id)
        .map((file) => ({ id: parseInt(file.id) })),
    };

    // The form slices initialise untouched fields to "", which a draft would
    // otherwise persist verbatim. Drop every blank so those columns hold NULL:
    // an empty string reads as set-but-blank (and sorts ahead of every real date
    // in the work order date filters), and numeric fields such as clientId are
    // validated with @IsNumber() server-side, which "" fails. Create-only —
    // edits go through buildEditPayload, where "" can mean "clear this".
    if (isDraft) {
      Object.keys(payload).forEach((field) => {
        if (payload[field] === "") delete payload[field];
      });
    }

    return payload;
  };

  const formatWorkOrderTasks = (tasksList) => {
    return tasksList.map((task) => {
      const {
        taskType,
        emailsToNotify,
        phone,
        reason,
        signatureFrom,
        title,
        description,
        ...rest
      } = task;
      const sanitizeEmails = emailsToNotify.map((emailObj) => emailObj.email);

      return {
        taskType,
        phone,
        reason,
        signatureFrom,
        title,
        description,
        emailsToNotify: sanitizeEmails,
      };
    });
  };

  const formatWorkOrderContacts = (contacts) => {
    return contacts
      ?.filter((contact) => contact.phoneNumber)
      .map((contact) => {
        const { name, phoneNumber, note, email, position } = contact;
        return {
          name,
          phoneNumber,
          note,
          email,
          position,
        };
      });
  };

  const formatWorkOrderExpenses = (expenses) => {
    return expenses
      ?.filter((expense) => expense.expenseType)
      ?.map((expense) => {
        const { expenseType, description, amount, id, expenseKey } = expense;
        return {
          id,
          expenseType,
          description,
          amount: parseFloat(amount),
          expenseKey,
        };
      });
  };

  /**
   * A work order stores qualifications as sub-types only — there is no link from
   * a work order to a qualification *type* (see schema: WorkOrderToQualification
   * SubType). So a type chosen with no sub-type under it has nowhere to be saved
   * and would disappear on the next load.
   *
   * Publishing already catches this through the form's own validation. Drafts
   * skip that, so check it here rather than dropping the row silently — losing
   * input without saying so is worse than asking for one more click.
   *
   * Returns false when the save should be stopped.
   */
  const assertQualificationsStorable = () => {
    const incomplete = (workOrder.scheduling?.qualifications || []).some(
      (qualification) =>
        qualification?.qualificationTypeId && !qualification?.qualificationSubTypes?.length,
    );

    if (!incomplete) return true;

    dispatch(updateWorkOrderFormValidation({ fieldName: "qualificationSubTypes", data: true }));
    dispatch(
      showToast({
        message:
          "Select at least one sub-type for each qualification, or remove it — a qualification can't be saved without one.",
        type: "error",
      }),
    );
    focusFirstInvalidField();
    return false;
  };

  const formatWorkOrderQualifications = (qualifications) => {
    const allSubTypes = qualifications
      .map((qualification) => qualification.qualificationSubTypes)
      .flat();

    return allSubTypes;
  };

  return (
    <div
      className={`border-b border-zinc-300 bg-white  sticky z-20 ${
        isBannerVisible ? "top-[107px]" : "top-[56px]"
      }`}
    >
      <BaseLoader isLoading={isPublishLoading || isSaving} />
      <BaseWrapper>
        <div className="grid grid-cols-7 py-[7px] items-center ">
          <div className="sm:col-span-3 col-span-7">
            <h3 className="font-light">{isEdit ? "Edit Work Order" : "New Work Order"}</h3>
          </div>
          <div className="sm:col-span-4 col-span-7  flex  items-center justify-end">
            {(!isEdit || isDraftEdit) && (
              <div className="mr-4 sm:w-36 w-full flex justify-end">
                <BaseButton
                  onClick={() => {
                    // Editing an existing draft saves in place; creating one POSTs
                    // a new work order with isDraft set.
                    if (isDraftEdit) {
                      editWorkWork();
                    } else {
                      handleWorkOrderPublish(true);
                    }
                  }}
                  style={{
                    border: isDraftReady() && "1px solid #0F40AE",
                    paddingTop: "5px",
                    paddingBottom: "5px",
                  }}
                  className="bg-[#F6F6F6]"
                  disabled={!isDraftReady() || isSaving}
                  fullWidth={true}
                >
                  <p className={`${!isDraftReady() ? "" : "text-primary cursor-pointer "}`}>
                    Save As Draft
                  </p>
                </BaseButton>
              </div>
            )}
            <div className="sm:w-24 w-full flex justify-end">
              <BaseButton
                fullWidth={true}
                loaderSize={22}
                isLoading={
                  isPublishLoading ||
                  isUploadWorkOrderCustomerFilesApiLoading ||
                  isApproveAndPublishLoading ||
                  isPublishDraftLoading ||
                  isSaving
                }
                variant="contained"
                color="primary"
                style={{
                  paddingTop: "6px",
                  paddingBottom: "6px",
                  paddingLeft: "22px",
                  paddingRight: "22px",
                }}
                onClick={() => {
                  if (isPendingApprovalEdit) {
                    handlePendingApprovalPublish();
                  } else if (isDraftEdit) {
                    handleSaveAndPublishDraft();
                  } else {
                    handleWorkOrderUpdateOrPublish(false);
                  }
                }}
                disabled={
                  isCustomer
                    ? !isCustomerPublishReady()
                    : isPendingApprovalEdit || isDraftEdit || !isEdit
                      ? // Anything that publishes — client review of a customer
                        // ticket, publishing a draft, or a fresh create — stays
                        // disabled until every required field is filled. A plain
                        // edit/Update of a live work order is never blocked.
                        !isPublishedButtonDisabled() || isSaving
                      : false
                }
              >
                <p
                  className={
                    (
                      isCustomer
                        ? !isCustomerPublishReady()
                        : isPendingApprovalEdit || isDraftEdit || !isEdit
                          ? !isPublishedButtonDisabled()
                          : false
                    )
                      ? ""
                      : "text-white cursor-pointer"
                  }
                >
                  {/* "Update" belongs to a live work order. A draft is not live yet,
                      so it publishes like a new one. */}
                  {isEdit && !isPendingApprovalEdit && !isDraftEdit
                    ? "Update"
                    : isCustomer
                      ? "Submit"
                      : "Publish"}
                </p>
              </BaseButton>
            </div>
          </div>
        </div>
      </BaseWrapper>

      {isPendingProposalWarningOpen && (
        <BaseConfirmModal
          open={isPendingProposalWarningOpen}
          onClose={() => setIsPendingProposalWarningOpen(false)}
          onConfirm={async () => {
            setIsPendingProposalWarningOpen(false);
            // Re-checked, in case the customer answered while this was open.
            await handlePendingApprovalPublish({ isPendingProposalConfirmed: true });
          }}
          title="Proposal Pending"
          description={
            <p>
              Your proposal to the customer is still pending. If you still want to publish, the work
              order will be published with the customer&apos;s current rates and the customer will
              no longer be able to respond to the proposal.
            </p>
          }
          confirmText="Publish Anyway"
          type="warning"
        />
      )}

      {isStaleRatesWarningOpen && (
        <BaseConfirmModal
          open={isStaleRatesWarningOpen}
          onClose={() => setIsStaleRatesWarningOpen(false)}
          onConfirm={() => navigateWithReload(`${location.pathname}${location.search}`)}
          title="Rates Updated"
          description={
            <p>
              The customer accepted your proposal after you opened this page, so the customer rates
              have changed. Reload to review the updated rates before publishing. Unsaved changes on
              this page will be lost.
            </p>
          }
          confirmText="Reload"
          type="warning"
        />
      )}
    </div>
  );
};

export default WorkOrderHeader;
